import { useEffect, useState } from "react";
import { api, CATEGORY_COLORS } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function Preliminary() {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState({});

  const load = () => api.get("/competitors").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <h1 className="font-display font-black text-3xl tracking-tight" data-testid="preliminary-round-title">Timing Round</h1>
      <p className="text-[#7A7D8C] mt-1 text-sm" data-testid="preliminary-instructions">Record one Timing Solve per participant to automatically assign a speed category. Corrections recalculate the category. Save a blank time to record DNF.</p>

      <div className="mt-6 bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="prelim-table">
            <thead>
              <tr className="bg-[#FDFFFC] text-[#7A7D8C] text-xs uppercase tracking-wider">
                <Th>Competitor</Th><Th>Parent-selected</Th><Th>Timing Solve</Th><Th>Recommended</Th><Th>Assigned</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-[#7A7D8C]">Add competitors first.</td></tr>
              )}
              {rows.map((r) => <PrelimRow key={r.id} row={r} saving={saving[r.id]} setSaving={(v) => setSaving((s) => ({ ...s, [r.id]: v }))} onDone={load} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-3 py-3 font-semibold">{children}</th>;

function PrelimRow({ row, saving, setSaving, onDone }) {
  const { ALL_CATEGORIES, CATEGORY_LABEL } = useCategories();
  const existing = row.prelim_times && row.prelim_times.length > 0 && row.prelim_times[0] != null ? String(row.prelim_times[0]) : "";
  const [t, setT] = useState(existing);
  const [assigned, setAssigned] = useState(row.assigned_category || "");

  useEffect(() => { setAssigned(row.assigned_category || ""); }, [row.assigned_category]);
  const save = async () => {
    setSaving(true);
    try {
      const time = t.trim() === "" ? null : Number(t);
      if (time !== null && (!Number.isFinite(time) || time <= 0)) {
        toast.error("Enter a positive Timing Solve in seconds."); return;
      }
      const { data } = await api.put(`/competitors/${row.id}/prelim`, { times: [time] });
      toast.success(`${row.full_name}: ${data.prelim_average ?? "DNF"} → ${data.recommended_category ? CATEGORY_LABEL[data.recommended_category] : "—"}`);
      if (!data.recommended_category && assigned && assigned !== row.assigned_category && assigned !== data.assigned_category) {
        await api.patch(`/competitors/${row.id}`, { assigned_category: assigned });
      }
      onDone();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const acceptRecommended = async () => {
    if (!row.recommended_category) return;
    await api.patch(`/competitors/${row.id}`, { assigned_category: row.recommended_category });
    toast.success("Accepted recommendation");
    onDone();
  };

  const mismatched = row.parent_selected_category && row.recommended_category && row.parent_selected_category !== row.recommended_category;

  return (
    <tr className="border-t border-slate-100 hover:bg-[#FFBF69]/5" data-testid={`prelim-row-${row.id}`}>
      <td className="px-3 py-3">
        <div className="font-semibold">{row.full_name}</div>
        {row.category_review_required && <div className="mt-1 text-xs font-semibold text-[#B36906]" data-testid={`category-review-${row.id}`}>Timing Solve is 121s or above — manual placement needed</div>}
        {mismatched && <div className="text-[10px] uppercase font-bold text-[#E71D36] mt-0.5">Mismatch</div>}
      </td>
      <td className="px-3 py-3"><CatBadge value={row.parent_selected_category} /></td>
      <td className="px-2 py-3">
        <input data-testid={`prelim-t1-${row.id}`} value={t} onChange={(e) => setT(e.target.value)} placeholder="s.ms" className="w-24 rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C]" />
      </td>
      <td className="px-3 py-3"><CatBadge value={row.recommended_category} /></td>
      <td className="px-3 py-3">
        <select value={assigned} disabled={Number(t) > 0 && Number(t) < 121} title={Number(t) > 0 && Number(t) < 121 ? "Assigned automatically when the Timing Solve is saved" : "Manual category placement"} onChange={(e) => setAssigned(e.target.value)} data-testid={`prelim-assign-${row.id}`} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm bg-white">
          <option value="">—</option>
          {ALL_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
        </select>
      </td>
      <td className="px-3 py-3">
        <div className="flex gap-1 justify-end">
          {row.recommended_category && row.assigned_category !== row.recommended_category && (
            <button onClick={acceptRecommended} data-testid={`prelim-accept-${row.id}`} className="p-2 rounded-lg text-[#2EC4B6] hover:bg-[#2EC4B6]/10" title="Accept recommendation"><CheckCircle2 className="w-4 h-4" /></button>
          )}
          <button onClick={save} disabled={saving} data-testid={`prelim-save-${row.id}`} className="px-3 py-1.5 rounded-full bg-[#FF9F1C] text-white text-xs font-bold disabled:opacity-60">{saving ? "…" : "Save"}</button>
        </div>
      </td>
    </tr>
  );
}

function CatBadge({ value }) {
  const { CATEGORY_LABEL } = useCategories();
  if (!value) return <span className="text-xs text-[#7A7D8C]">—</span>;
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${CATEGORY_COLORS[value] || ""}`}>{CATEGORY_LABEL[value]}</span>;
}
